// Mobile bottom bar (< 768 px, IA §2.3): 4 role-adaptive destinations + More. More opens a
// full-height sheet with the complete (permission-filtered) tree and a search field.
import { useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { t, type PlainKey } from "@ady/i18n";
import { BrandIcon, cx, Drawer, Field, Icon, Input } from "@ady/ui";
import { TID } from "../testids";
import { AppLink } from "./AppLink";
import { useShell } from "./context";
import { bottomBarItems, brandFor, hrefFor, ICON_FOR_ITEM, visibleNav } from "./nav";

const SHORT_LABEL: Record<string, PlainKey> = {
  today: "nav.groups.today",
  orders: "nav.groups.orders",
  pos: "nav.groups.sell",
  sales: "nav.bottom.sales",
  registers: "nav.bottom.drawer",
  products: "nav.groups.products",
  customers: "nav.groups.customers",
  returns: "nav.items.returns",
  reports: "nav.groups.reports",
  money: "nav.groups.money",
};

export function BottomBar() {
  const { membership, orgSlug, storeSlug } = useShell();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState("");
  const items = bottomBarItems(membership);
  const groups = useMemo(() => visibleNav(membership), [membership]);
  const q = query.trim().toLowerCase();
  const cell =
    "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-2xs font-medium";
  return (
    <>
      <nav
        aria-label={t("shell.mainNav")}
        data-testid={TID.bottomBar}
        className="fixed inset-x-0 bottom-0 z-(--ady-z-bottom-nav) flex h-(--ady-bottom-bar-h) border-t border-subtle bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {items.map((item) => {
          const href = hrefFor(item, orgSlug, storeSlug);
          const current = pathname === href || pathname.startsWith(`${href}/`);
          return (
            <AppLink
              key={item.id}
              href={href}
              data-testid={TID.bottomItem(item.id)}
              aria-current={current ? "page" : undefined}
              className={cx(cell, "no-underline", current ? "text-action-fg" : "text-fg-secondary")}
            >
              {brandFor(item) ? (
                <BrandIcon brand={brandFor(item)!} size={22} />
              ) : (
                <Icon
                  name={ICON_FOR_ITEM[item.id] ?? "chevronRight"}
                  size={22}
                  gradient={current}
                />
              )}
              <span className={cx("text-center", current && "ady-text-brand")}>
                {t(SHORT_LABEL[item.id] ?? item.label)}
              </span>
            </AppLink>
          );
        })}
        <button
          type="button"
          data-testid={TID.bottomMore}
          className={cx(cell, "text-fg-secondary")}
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <Icon name="menu" size={22} />
          <span>{t("common.more")}</span>
        </button>
      </nav>

      <Drawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        side="bottom"
        title={t("shell.moreTitle")}
        data-testid={TID.moreSheet}
      >
        <Field label={t("shell.moreSearch")} className="pb-4">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} type="search" />
        </Field>
        <div className="flex flex-col gap-4">
          {groups.map((g) => {
            const matches = g.items.filter(
              (i) => !q || `${t(i.label)} ${t(g.label)}`.toLowerCase().includes(q),
            );
            if (!matches.length) return null;
            return (
              <section key={g.id}>
                <h3 className="flex items-center gap-2 pb-1 text-sm font-medium text-fg-secondary">
                  {g.brand ? (
                    <BrandIcon brand={g.brand} size={18} />
                  ) : (
                    <Icon name={g.icon} size={18} />
                  )}
                  {t(g.label)}
                </h3>
                <ul>
                  {matches.map((item) => (
                    <li key={item.id}>
                      <AppLink
                        href={hrefFor(item, orgSlug, storeSlug)}
                        onClick={() => setMoreOpen(false)}
                        data-testid={TID.moreItem(item.id)}
                        className="flex min-h-12 items-center rounded-sm px-3 text-md text-fg no-underline hover:bg-hover"
                      >
                        {t(item.label)}
                      </AppLink>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </Drawer>
    </>
  );
}
